import {
  FulfillmentTypeListResponseType,
  BuyerProductGetResponseChildType,
  SupplierGetResponseType,
  SupplierStorefrontV2GetResponseType,
} from "@sellar-io/api-v2/types";
import { pluralize } from "@sellar-io/common";
import { twMerge } from "tailwind-merge";
import { CheckoutSelectedFulfillmentType } from "../../pages";
import { Icon } from "@sellar-io/components";

import { CollectionOptionsCard } from "./CollectionOptionsCard";
import {
  CourierDeliveryOptionsCard,
  DeliveryOrderItemType,
} from "./CourierDeliveryOptionsCard";
import { DirectDeliveryOptionsCard } from "./DirectDeliveryOptionsCard";
import { HaulierDeliveryOptionsCard } from "./HaulierDeliveryOptionsCard";
import { useEffect } from "react";
import { HaulierTierInfoCard } from "../../pages/stockList/Components/HaulierTierInfoCard";
import { DistributionDeliveryOptionsCard } from "./DistributionDeliveryOptionsCard";
import { useStorefrontBuyerPostcode } from "../../pages/stockList/categoryPage/Storefront/lib/useStorefrontBuyerPostcode";

export const SelectedFulfillmentTypeCard = ({
  selectedFulfillmentType,
  supplier,
  supplierStorefront,
  orderItems,
  onClick,
  changeFulfillmentType,
  productItem,
}: {
  selectedFulfillmentType?: CheckoutSelectedFulfillmentType;
  supplier: SupplierGetResponseType;
  supplierStorefront: SupplierStorefrontV2GetResponseType;
  orderItems?: DeliveryOrderItemType;
  onClick?: () => void;
  changeFulfillmentType?: (
    fulfillmentType: CheckoutSelectedFulfillmentType,
  ) => void;
  productItem?: Pick<
    BuyerProductGetResponseChildType,
    "containerType" | "unit" | "volume"
  >;
}) => {
  const selectedFulfillmentTypeValue =
    selectedFulfillmentType ??
    supplierStorefront.FulfillmentType.fulfillmentType ??
    undefined;

  const deliveryMethods = supplierStorefront.deliveryMethods ?? [];
  const { buyerPostcode } = useStorefrontBuyerPostcode();
  const sdDeliveryArea = supplierStorefront.sdDeliveryArea;
  const directDeliveryArea = supplierStorefront.directDeliveryArea;

  const containerType = productItem?.containerType ?? null;

  const availableDeliveryMethods = deliveryMethods.filter((dm) => {
    if (!containerType || !dm.DeliveryMethodContainerRules) return true;
    const isUnavailable = dm.DeliveryMethodContainerRules.some(
      (rule) => rule.containerType === containerType,
    );
    return !isUnavailable;
  });

  const selectedMethod = availableDeliveryMethods.find(
    (dm) => dm.fulfillmentType === selectedFulfillmentTypeValue,
  );

  useEffect(() => {
    if (
      selectedFulfillmentTypeValue &&
      !selectedMethod &&
      availableDeliveryMethods.length > 0
    ) {
      const firstAvailable = availableDeliveryMethods[0]?.fulfillmentType;
      if (firstAvailable) changeFulfillmentType?.(firstAvailable);
    }
  }, [
    selectedFulfillmentTypeValue,
    selectedMethod,
    availableDeliveryMethods,
    changeFulfillmentType,
  ]);

  const toFulfillmentTypeProps = (
    dm: (typeof deliveryMethods)[number],
  ): Omit<
    FulfillmentTypeListResponseType[number],
    | "id"
    | "name"
    | "OrderRules"
    | "numberOfCustomers"
    | "numberOfZones"
    | "DeliveryMethodContainerRules"
  > => ({
    ...dm,
    fulfillmentType:
      dm.fulfillmentType as FulfillmentTypeListResponseType[number]["fulfillmentType"],
    DeliveryChargeRules: [],
  });

  const otherAvailableOptionsCount = availableDeliveryMethods.filter(
    (dm) => dm.fulfillmentType !== selectedFulfillmentTypeValue,
  ).length;

  const unbundlingEnabled = Boolean(supplierStorefront.deliveryMethods);

  if (unbundlingEnabled && availableDeliveryMethods.length === 0) {
    return (
      <div className="flex flex-col gap-4 rounded-lg border border-gray-200 p-6">
        <div className="flex flex-col gap-2">
          <h3 className="font-semibold text-gray-900">
            Unable to purchase from {supplier.Business.name}
          </h3>
          <p className="text-sm text-gray-600">
            {supplier.Business.name} is not currently available for online
            orders. You can contact them to let them know you're interested in
            purchasing from them.
          </p>
        </div>
      </div>
    );
  }

  if (selectedFulfillmentTypeValue === "direct" && selectedMethod) {
    return (
      <>
        <DirectDeliveryOptionsCard
          directFulfillmentType={toFulfillmentTypeProps(selectedMethod)}
          supplier={supplier}
          deliveryArea={directDeliveryArea}
          orderItems={orderItems}
        />
        {!!otherAvailableOptionsCount && (
          <OtherOptionsAvailableButton
            otherAvailableOptionsCount={otherAvailableOptionsCount}
            onClick={onClick}
          />
        )}
      </>
    );
  }

  if (selectedFulfillmentTypeValue === "sellarDistribution" && selectedMethod) {
    return (
      <>
        <DistributionDeliveryOptionsCard
          directFulfillmentType={toFulfillmentTypeProps(selectedMethod)}
          supplier={supplier}
          deliveryArea={sdDeliveryArea}
          orderItems={orderItems}
          buyerPostcode={buyerPostcode}
        />
        {!!otherAvailableOptionsCount && (
          <OtherOptionsAvailableButton
            otherAvailableOptionsCount={otherAvailableOptionsCount}
            onClick={onClick}
          />
        )}
      </>
    );
  }

  if (selectedFulfillmentTypeValue === "courier" && selectedMethod) {
    return (
      <>
        <CourierDeliveryOptionsCard
          courierFulfillmentType={toFulfillmentTypeProps(selectedMethod)}
          supplier={supplier}
          orderItems={orderItems}
        />
        {!!otherAvailableOptionsCount && (
          <OtherOptionsAvailableButton
            otherAvailableOptionsCount={otherAvailableOptionsCount}
            onClick={onClick}
          />
        )}
      </>
    );
  }

  if (selectedFulfillmentTypeValue === "haulier" && selectedMethod) {
    return (
      <>
        <HaulierDeliveryOptionsCard
          haulierFulfillmentType={toFulfillmentTypeProps(selectedMethod)}
          supplierId={supplier.id}
        />
        <HaulierTierInfoCard
          fulfillmentType={toFulfillmentTypeProps(selectedMethod)}
          compact={true}
          supplierStorefront={supplierStorefront}
        />
        {!!otherAvailableOptionsCount && (
          <OtherOptionsAvailableButton
            otherAvailableOptionsCount={otherAvailableOptionsCount}
            onClick={onClick}
          />
        )}
      </>
    );
  }

  if (selectedFulfillmentTypeValue === "collection") {
    return (
      <>
        <CollectionOptionsCard supplier={supplier} orderItems={orderItems} />
        {!!otherAvailableOptionsCount && (
          <OtherOptionsAvailableButton
            otherAvailableOptionsCount={otherAvailableOptionsCount}
            onClick={onClick}
          />
        )}
      </>
    );
  }

  return null;
};

const OtherOptionsAvailableButton = ({
  otherAvailableOptionsCount,
  onClick,
}: {
  otherAvailableOptionsCount: number;
  onClick?: () => void;
}) => {
  return (
    <div
      className={twMerge(
        "mt-3 flex flex-row justify-between rounded-lg border border-zinc-300 bg-zinc-100 p-2 text-zinc-800",
        onClick && "cursor-pointer",
      )}
      onClick={() => onClick?.()}
    >
      <p>
        {" "}
        +{otherAvailableOptionsCount} more delivery{" "}
        {pluralize("option", otherAvailableOptionsCount)}
      </p>
      {onClick && (
        <p className={"m-0 flex flex-row items-center gap-2 p-0 text-blue-800"}>
          <span className={"font-semibold"}>SWITCH</span>
          <Icon icon={"SYNC"} />
        </p>
      )}
    </div>
  );
};
